<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <div class="steps-container venn-diagram-container" :class="{ isMobile }" slot="content">
        <div :class="{ scaled: true, 'chromeos-scale': isChromeOS, 'mobile-scale': isMobile }">
          <div class="venn-diagram-circles-container">
            <svg
              v-html="vennDiagramSVG"
              id="venn-diagram-circles"
              height="460px"
              width="980px"
            ></svg>
          </div>
          <article id="paragraphs-container">
            <div class="left-paragraph-container step">
              <div class="venn-image-left">
                <img
                  v-if="leftRound?.vennDiagramOptionalImage"
                  alt="Left image"
                  :src="leftRound.vennDiagramOptionalImage?.image?.src"
                />
              </div>
              <div
                :key="stepIndex"
                v-for="(step, stepIndex) in leftRound?.steps"
                class="left-paragraph"
              >
                <blinking-cursor-text
                  :text-step="stepIndex"
                  :text="step.answer"
                  :round-id="0"
                  :shouldBeCompletedStep="progress.roundIndex > 0 || progress.stepIndex > stepIndex"
                >
                </blinking-cursor-text>
              </div>
            </div>
            <div class="center-paragraph-container step">
              <div class="venn-image-center">
                <img
                  v-if="centerRound?.vennDiagramOptionalImage"
                  alt="Center image"
                  :src="centerRound.vennDiagramOptionalImage?.image?.src"
                />
              </div>
              <div
                :key="stepIndex"
                v-for="(step, stepIndex) in centerRound?.steps"
                class="center-paragraph"
              >
                <blinking-cursor-text :text-step="stepIndex" :text="step.answer" :round-id="2">
                </blinking-cursor-text>
              </div>
            </div>
            <div class="right-paragraph-container step">
              <div class="venn-image-right">
                <img
                  v-if="rightRound?.vennDiagramOptionalImage"
                  alt="Right image"
                  :src="rightRound.vennDiagramOptionalImage?.image?.src"
                />
              </div>
              <div
                :key="stepIndex"
                v-for="(step, stepIndex) in rightRound?.steps"
                class="right-paragraph"
              >
                <blinking-cursor-text
                  :text-step="stepIndex"
                  :text="step.answer"
                  :round-id="1"
                  :shouldBeCompletedStep="progress.roundIndex > 1 || progress.stepIndex > stepIndex"
                >
                </blinking-cursor-text>
              </div>
            </div>
          </article>
        </div>
      </div>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';
import vennDiagramSVG from 'assets/svg/venn-diagram.svg';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import { createNamespacedHelpers } from 'vuex';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import { VennDiagramPosition } from 'graph/types/schema-generated-types';

const application = createNamespacedHelpers('application');

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    ActivityStage,
    BlinkingCursorText
  },

  data() {
    return {
      vennDiagramSVG
    };
  },
  computed: {
    ...application.mapState({
      isChromeOS: state => state.device.isChromeOS,
      isMobile: state => state.device.isMobile
    }),
    leftRound() {
      return this.filterByPosition(VennDiagramPosition.LEFT);
    },
    centerRound() {
      return this.filterByPosition(VennDiagramPosition.CENTER);
    },
    rightRound() {
      return this.filterByPosition(VennDiagramPosition.RIGHT);
    }
  },
  methods: {
    filterByPosition(position) {
      if (!this.rounds.length) {
        return { steps: [] };
      }
      return this.rounds.find(r => r.position === position);
    },

    getCurrentDOM() {
      switch (this.progress.roundIndex) {
        case 0:
          return '.left-paragraph-container';
        case 1:
          return '.right-paragraph-container';
        default:
          return '.center-paragraph-container';
      }
    },

    async loadActivity() {
      await this.loadImages();
      this.createRounds();
    },

    async loadImages() {
      const { rounds } = this.activityData;

      rounds.forEach(({ vennDiagramOptionalImage }) => {
        this.queueImage(vennDiagramOptionalImage?.image?.src);
      });

      await this.downloadImages();
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
      this.$nextTick(() => {
        TweenMax.to(this.getCurrentDOM(), 1, { css: { opacity: 1 } });
      });
      this.$nextTick(() => {
        this.setActivityContainerHeight();
        this.setVennDiagramCirclesHeight();
      });
    },

    handleNewRound() {
      this.setupNewRound();
      this.$nextTick(() => {
        TweenMax.to(this.getCurrentDOM(), 1, { css: { opacity: 1 } });
      });
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        hideNumberRow: false,
        keyboardType,
        roofs
      };
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();

        if (this.progress.stepIndex >= this.currentRound?.steps?.length) {
          TweenMax.to(this.getCurrentDOM(), 1, { css: { opacity: 1 } });

          if (this.isEndOfActivity) {
            this.playCongrats();
            TweenMax.delayedCall(1, () => this.completeActivity(false));
            return;
          }
          this.handleNewRound();
        }
      }
    },

    setVennDiagramCirclesHeight() {
      const vennDiagramCirclesEl = document.querySelector('#venn-diagram-circles');

      if (vennDiagramCirclesEl && this.isMobile) {
        vennDiagramCirclesEl.setAttribute('height', '590px');
      }
    }
  }
};
</script>
<style lang="scss" scoped>
#paragraphs-container {
  align-items: center;
  display: flex;
  justify-content: center;
  height: 467px;
  left: 0px;
  position: absolute;
  top: 0px;

  width: 1024px;
}

.scaled {
  text-align: center;
  transform: scale(0.8);
  transform-origin: top;

  .step {
    text-align: left;
  }
}

.chromeos-scale {
  transform: scale(0.675);
}

.mobile-scale {
  transform: scale(0.65);

  #paragraphs-container {
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
}

.left-paragraph {
  margin-left: -15px;
}

.center-paragraph {
  margin-left: 15px;
}

.left-paragraph-container {
  height: 300px;
  left: 24px;
  opacity: 0;
  position: relative;
  width: 300px;

  img {
    left: 10px;
    position: relative;
  }
}

.center-paragraph-container {
  opacity: 0;
}

.right-paragraph-container {
  opacity: 0;
  left: 70px;
  width: 300px;
  height: 300px;
  position: relative;

  img {
    left: 10px;
    position: relative;
  }
}

.venn-diagram-container {
  width: 100%;
  height: 100%;
  position: relative;

  .venn-diagram-circles-container {
    padding: 0 20px;
  }

  p {
    font-size: 20px;
  }

  .venn-image-left,
  .venn-image-right {
    width: 180px;
    height: 110px;
    position: relative;
    margin-bottom: 10px;
  }

  .venn-image-right {
    left: 10px;
  }

  img {
    width: 180px;
  }
}

.chrome-os {
  .venn-diagram-container {
    transform: scale(0.85);
  }
}
</style>
